/**
 * EmployeeCandidateSelect — wybór pracownika powiązanego z kontem użytkownika (pole core
 * `employee_id` formatki Użytkownika).
 *
 * Buduje na `SearchableSelect` w trybie serwerowym: lista kandydatów pochodzi z
 * `GET /api/users/:id/employee-candidates?search=` (fraza z debounce 300 ms), pobierana dopiero po
 * otwarciu pola. Serwer zwraca wyłącznie pracowników z firm, do których edytowane konto ma dostęp.
 *
 * Kandydat zajęty przez INNE konto (`linkedUser`) jest widoczny, ale niedostępny do wyboru, z
 * notką „ma już konto: …"; nieaktywny dostaje plakietkę „nieaktywny". Wyczyszczenie (X) = odpięcie
 * (`onChange(null)` → PATCH `employeeId: null`).
 *
 * Etykieta wybranej wartości nie zależy od bieżącej listy: po wyborze pamiętamy etykietę
 * klikniętej opcji (`pickedEmployee` z rodzica — przeżywa przełączenie podgląd/edycja i odświeżenie
 * rekordu po zapisie; bez niego lokalny stan), a przed pierwszym wyborem bierzemy ją z podsumowania
 * zapisanego pracownika (`currentEmployee`, z GET rekordu) — lista kandydatów bywa zawężona frazą
 * albo ucięta limitem.
 *
 * Błąd pobierania listy nie udaje „Brak wyników": pole pokazuje komunikat (403 = brak uprawnień do
 * zmiany powiązania, inaczej ogólny błąd wczytania).
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { SearchableSelect, type SearchableSelectOption } from '@/components/SearchableSelect';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { isForbiddenError } from '@/lib/query';
import { type UserEmployeeSummary, useUserEmployeeCandidates } from '@/lib/use-tenant-people';

const SEARCH_DEBOUNCE_MS = 300;

/** Pracownik wybrany z listy w tej sesji edycji — id + gotowa etykieta „Imię Nazwisko — Firma". */
export interface PickedEmployee {
  id: string;
  label: string;
}

/** Etykieta pracownika w polu i na liście: „Imię Nazwisko — Firma" (bez firmy, gdy jej brak). */
export function employeeOptionLabel(
  t: TFunction,
  employee: { firstName: string; lastName: string; companyName: string | null },
): string {
  const name = `${employee.firstName} ${employee.lastName}`.trim();
  if (!employee.companyName) return name;
  return t('tenant.users.detail.employee.optionLabel', { name, company: employee.companyName });
}

export interface EmployeeCandidateSelectProps {
  /** Edytowane konto — kontekst zapytania o kandydatów (firmy dostępne dla tego konta). */
  userId: string;
  value: string | null;
  onChange: (employeeId: string | null) => void;
  /** Zapisany pracownik z GET rekordu — etykieta triggera, gdy nie ma go w liście kandydatów. */
  currentEmployee: UserEmployeeSummary | null;
  /** Ostatnio wybrany z listy pracownik, trzymany przez rodzica (patrz nagłówek pliku). */
  pickedEmployee?: PickedEmployee | null;
  /** Wołany po wyborze pracownika z listy — rodzic zapamiętuje etykietę (`pickedEmployee`). */
  onPick?: (picked: PickedEmployee) => void;
  disabled?: boolean;
  'data-testid'?: string;
}

export function EmployeeCandidateSelect({
  userId,
  value,
  onChange,
  currentEmployee,
  pickedEmployee,
  onPick,
  disabled,
  'data-testid': testId = 'user-field-employeeId',
}: EmployeeCandidateSelectProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Etykieta ostatnio klikniętej opcji — po wyborze lista wraca do widoku bazowego (fraza zerowana),
  // w którym wybranego może już nie być. Lokalny stan to zapas, gdy rodzic nie trzyma wyboru.
  const [localPicked, setLocalPicked] = useState<PickedEmployee | null>(null);
  const picked = pickedEmployee ?? localPicked;
  const trimmedSearch = search.trim();
  const debouncedSearch = useDebouncedValue(trimmedSearch, SEARCH_DEBOUNCE_MS);

  const candidatesQ = useUserEmployeeCandidates(userId, debouncedSearch, isOpen);

  // Błąd pobierania ≠ pusta lista: 403 (brak prawa edycji użytkowników) ma osobny, jasny komunikat.
  // Ponowna próba po błędzie (np. ponowne otwarcie pola) zeruje błąd zapytania bez danych, więc pole
  // pokazuje wtedy „Ładowanie…", nie dawny komunikat (test „ponowne pobranie po błędzie").
  const loadErrorMessage = candidatesQ.isError
    ? isForbiddenError(candidatesQ.error)
      ? t('tenant.users.detail.employee.noPermission')
      : t('tenant.users.detail.employee.loadError')
    : undefined;

  const options = useMemo<SearchableSelectOption[]>(
    () =>
      (candidatesQ.data?.candidates ?? []).map((c) => ({
        value: c.id,
        label: employeeOptionLabel(t, c),
        description: c.linkedUser
          ? t('tenant.users.detail.employee.linkedNote', {
              account: c.linkedUser.displayName?.trim() || c.linkedUser.username || '',
            })
          : undefined,
        badges: c.isActive
          ? undefined
          : [{ label: t('tenant.users.detail.employee.inactiveNote'), tone: 'warning' as const }],
        disabled: c.linkedUser !== null,
      })),
    [candidatesQ.data, t],
  );

  const selectedLabel =
    value === null
      ? undefined
      : picked?.id === value
        ? picked.label
        : currentEmployee?.id === value
          ? employeeOptionLabel(t, currentEmployee)
          : undefined;

  return (
    <SearchableSelect
      value={value}
      onChange={(next) => {
        if (next !== null) {
          const opt = options.find((o) => o.value === next);
          if (opt) {
            const pick: PickedEmployee = { id: next, label: opt.label };
            setLocalPicked(pick);
            onPick?.(pick);
          }
        }
        onChange(next);
      }}
      options={options}
      disabled={disabled}
      allowClear
      placeholder={t('tenant.users.detail.employee.placeholder')}
      aria-label={t('tenant.users.detail.employee.ariaLabel')}
      data-testid={testId}
      onOpenChange={setIsOpen}
      onSearchChange={setSearch}
      filterLocally={false}
      // Spinner także w oknie debounce (fraza wpisana, zapytanie jeszcze nie poszło) — bez
      // „Brak wyników" mignącego między wpisaniem a odpowiedzią.
      loading={candidatesQ.isFetching || trimmedSearch !== debouncedSearch}
      emptyMessage={loadErrorMessage}
      selectedLabel={selectedLabel}
    />
  );
}
